import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Wallet, Plus, Trash2, Loader2, Save, Fuel, Wrench, Home, CreditCard, Car, Users,
  Phone, ShieldCheck, Building2, Banknote, FileText, Package, Receipt,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { VariableCostTrend } from "./VariableCostTrend";
import { Money } from "./SaudiRiyal";
import { api } from "../lib/api";

const ICONS = {
  receipt: Receipt, home: Home, id: CreditCard, fuel: Fuel, wrench: Wrench, car: Car, users: Users,
  phone: Phone, shield: ShieldCheck, wallet: Wallet, building: Building2, banknote: Banknote, file: FileText, package: Package,
};
const ICON_OPTS = [
  ["receipt", "عام"], ["id", "إقامة/رخصة"], ["home", "سكن"], ["fuel", "بنزين"], ["wrench", "صيانة"],
  ["car", "سيارة"], ["users", "رواتب"], ["phone", "اتصالات"], ["shield", "تأمين"], ["wallet", "سلفة/محفظة"],
  ["building", "إيجار"], ["banknote", "نقد"], ["file", "مستند"], ["package", "أخرى"],
];
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));
const Ico = ({ k, className }) => { const C = ICONS[k] || Receipt; return <C className={className} />; };

export const RiderCostCard = ({ canEdit }) => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [income, setIncome] = useState(0);
  const [incomeLabel, setIncomeLabel] = useState("إيراد هنجر");
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [impBusy, setImpBusy] = useState("");

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const d = await api.getRiderCost(m);
      setIncome(d.income || 0);
      setIncomeLabel(d.income_label || "إيراد هنجر");
      setExpenses((d.expenses || []).map((e) => ({ ...e, id: e.id || newId() })));
    } catch { toast.error("تعذّر تحميل كلفة المندوب"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(month); }, [month, load]);

  const setItem = (id, field, val) => setExpenses((xs) => xs.map((e) => (e.id === id ? { ...e, [field]: val } : e)));
  const addItem = () => setExpenses((xs) => [...xs, { id: newId(), icon: "receipt", label: "", amount: 0 }]);
  const delItem = (id) => setExpenses((xs) => xs.filter((e) => e.id !== id));

  const upsertLine = (icon, label, amount) => setExpenses((xs) => {
    const i = xs.findIndex((e) => e.label === label);
    if (i >= 0) { const c = [...xs]; c[i] = { ...c[i], amount }; return c; }
    return [...xs, { id: newId(), icon, label, amount }];
  });

  const onImport = async (e, kind) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setImpBusy(kind);
    try {
      const r = await api.importRiderCostExcel(f);
      if (kind === "fuel") upsertLine("fuel", "البنزين", r.total);
      else upsertLine("wrench", "الصيانة", r.total);
      toast.success(`تم استيراد ${kind === "fuel" ? "تكلفة البنزين" : "تكلفة الصيانة"}: ${r.total} ⃁ (${r.count} صف)`);
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر قراءة الملف"); }
    finally { setImpBusy(""); }
  };

  const importFuelFromApp = async () => {
    setImpBusy("fuel");
    try {
      const r = await api.getRiderCostFuelTotal(month);
      if (!r.count) { toast.info(`لا توجد بيانات وقود مسجّلة في قسم تكلفة الوقود لشهر ${month}`); return; }
      upsertLine("fuel", "البنزين", r.total);
      toast.success(`تم استيراد تكلفة البنزين من البرنامج: ${r.total} ⃁ (${r.count} عملية · ${r.liters} لتر). لا تنسَ الحفظ.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر جلب تكلفة البنزين"); }
    finally { setImpBusy(""); }
  };

  const totalExp = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.saveRiderCost({ month, income: Number(income || 0), income_label: incomeLabel, expenses });
      toast.success("تم حفظ كلفة المندوب");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4" data-testid="rider-cost-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-600"><Wallet className="h-5 w-5" /></div>
          <div>
            <h3 className="font-heading text-base font-bold text-slate-900">المصروفات المتغيرة</h3>
            <p className="text-[11px] text-slate-500">بنود شهرية متغيرة (البنزين، الصيانة، وغيرها)</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="month" data-testid="rider-cost-month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm" />
          {canEdit && (
            <Button data-testid="rider-cost-save" disabled={saving} onClick={save} className="h-9 gap-1.5 bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : (
        <>
          <p className="text-sm text-slate-600">إجمالي المصروفات المتغيرة للشهر: <b className="text-red-600" data-testid="rider-cost-total"><Money value={totalExp} /></b></p>

          {canEdit && (
            <div className="flex items-center gap-2 flex-wrap">
              <input type="file" accept=".xlsx,.xls" id="rc-fuel" className="hidden" data-testid="rider-cost-fuel-input" onChange={(e) => onImport(e, "fuel")} />
              <Button variant="outline" disabled={impBusy === "fuel"} onClick={importFuelFromApp}
                className="h-8 gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="rider-cost-import-fuel">
                {impBusy === "fuel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fuel className="h-4 w-4" />} استيراد تكلفة البنزين من البرنامج
              </Button>
              <input type="file" accept=".xlsx,.xls" id="rc-maint" className="hidden" data-testid="rider-cost-maint-input" onChange={(e) => onImport(e, "maint")} />
              <Button variant="outline" disabled={impBusy === "maint"} onClick={() => document.getElementById("rc-maint").click()}
                className="h-8 gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="rider-cost-import-maint">
                {impBusy === "maint" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />} استيراد تكلفة الصيانة
              </Button>
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-2">
              <span className="text-sm font-bold text-slate-700">بنود المصروفات</span>
              {canEdit && (
                <Button size="sm" variant="ghost" onClick={addItem} className="h-7 gap-1 text-teal-700 hover:bg-teal-50" data-testid="rider-cost-add-item">
                  <Plus className="h-4 w-4" /> أضف بنداً
                </Button>
              )}
            </div>
            {expenses.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400" data-testid="rider-cost-empty">
                لا توجد بنود بعد. أضف بنداً مثل «الإقامة» أو «السكن» مع مبلغه، أو استورد البنزين والصيانة.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {expenses.map((e) => (
                  <div key={e.id} data-testid={`rider-cost-item-${e.id}`} className="flex items-center gap-2 px-3 py-2">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600"><Ico k={e.icon} className="h-4 w-4" /></div>
                    {canEdit ? (
                      <>
                        <select value={e.icon} onChange={(ev) => setItem(e.id, "icon", ev.target.value)}
                          className="h-9 w-24 rounded-lg border border-slate-200 bg-white px-1.5 text-xs" data-testid={`rider-cost-icon-${e.id}`}>
                          {ICON_OPTS.map(([k, name]) => <option key={k} value={k}>{name}</option>)}
                        </select>
                        <Input value={e.label} onChange={(ev) => setItem(e.id, "label", ev.target.value)} placeholder="اسم البند (مثال: الإقامة)"
                          className="h-9 flex-1 min-w-[120px]" data-testid={`rider-cost-label-${e.id}`} />
                        <Input type="number" dir="ltr" value={e.amount} onChange={(ev) => setItem(e.id, "amount", ev.target.value)}
                          className="h-9 w-28 text-center font-semibold" data-testid={`rider-cost-amount-${e.id}`} />
                        <button onClick={() => delItem(e.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" data-testid={`rider-cost-del-${e.id}`}><Trash2 className="h-4 w-4" /></button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm font-semibold text-slate-700">{e.label}</span>
                        <span className="font-mono-nums font-bold text-slate-800"><Money value={e.amount} /></span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      <VariableCostTrend month={month} key={`${month}-${saving}`} />
    </div>
  );
};
